/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-empty-interface */
/* eslint-disable react/no-unstable-nested-components */
import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { Provider } from "jotai";
import { merge } from "lodash/fp";
import { PauseIcon, Undo2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { DeepPartial } from "utility-types";
import RefreshButton from "components/financials/modern/RefreshButton";
import AddFinancialsTemplateMutation from "components/financials/modern/graphql/AddFinancialsTemplateMutation";
import EditFinancialsTemplateMutation from "components/financials/modern/graphql/EditFinancialsTemplateMutation";
import FinancialsTemplatesQuery from "components/financials/modern/graphql/FinancialsTemplatesQuery";
import { StarredTemplatesQuery } from "components/financials/modern/graphql/StarredTemplatesQuery";
import UserFinancialsTemplatesQuery from "components/financials/modern/graphql/UserFinancialsTemplatesQuery";
import type {
  IAddFinancialsTemplateMutation,
  IAddFinancialsTemplateMutationVariables,
} from "components/financials/modern/graphql/__generated__/AddFinancialsTemplateMutation.generated";
import type {
  IEditFinancialsTemplateMutation,
  IEditFinancialsTemplateMutationVariables,
} from "components/financials/modern/graphql/__generated__/EditFinancialsTemplateMutation.generated";
import type {
  IFinancialsTemplatesQuery,
  IFinancialsTemplatesQueryVariables,
} from "components/financials/modern/graphql/__generated__/FinancialsTemplatesQuery.generated";
import type {
  IStarredTemplatesQuery,
  IStarredTemplatesQueryVariables,
} from "components/financials/modern/graphql/__generated__/StarredTemplatesQuery.generated";
import type {
  IUserFinancialsTemplatesQuery,
  IUserFinancialsTemplatesQueryVariables,
} from "components/financials/modern/graphql/__generated__/UserFinancialsTemplatesQuery.generated";
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import DefineTemplateForm from "components/financials/modern/templates/DefineTemplateForm";
import QuickTemplateSwitcher from "components/financials/modern/templates/QuickTemplateSwitcher";
import TemplatesList from "components/financials/modern/templates/TemplatesList";
import { useTemplateActions } from "components/financials/modern/templates/hooks";
import { getPlatformVerticalTitle } from "components/financials/modern/utils/platformVertical";
import Filters, { QueryVariablesContext } from "components/pages/TemplatesGalleryPage/Filters";
import { Button } from "components/ui/Button";
import { NativeSelect } from "components/ui/Select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "components/ui/Sheet";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs/v2/TabsV2";
import useSelfie from "lib/hooks/useSelfie";
import type { IPlatformVertical } from "types/graphqlTypes";
import {
  IFinancialsTemplateNamespace,
  IFinancialsTemplateVisibility,
  IFinancialsTemplatesQueryOrderField,
  IIndicatorDataSource,
  IOrderDirection,
  ISecurityCountry,
} from "types/graphqlTypes";

export enum TemplatesEnum {
  Public = "PUBLIC",
  MyTemplates = "MY_TEMPLATES",
  StarredTemplates = "STARRED_TEMPLATES",
  ArchivedTemplates = "ARCHIVED_TEMPLATES",
}

interface TemplatesProps {
  vertical: IPlatformVertical;
  indicatorDataSource: IIndicatorDataSource;
  financialsTemplate: IFinancialsTemplateFieldsFragment | null;
  systemTemplate: IFinancialsTemplateFieldsFragment | null;
  overridingTemplate: IFinancialsTemplateFieldsFragment | null;
  renderingTemplate: IFinancialsTemplateFieldsFragment | null;
  onChange?: (template: IFinancialsTemplateFieldsFragment | null) => void;
}

function TemplatesSheetTriggerSkeleton() {
  return (
    <div
      className={classNames(
        "flex h-10 w-full animate-pulse flex-nowrap items-center rounded-xl bg-slate-200/60",
        "dark:bg-slate-950 dark:text-slate-300",
        "w-full lg:max-w-52",
      )}
    />
  );
}

export default function Templates({ systemTemplate, ...props }: TemplatesProps) {
  if (!systemTemplate) {
    return <TemplatesSheetTriggerSkeleton />;
  }
  return <TemplatesSheet systemTemplate={systemTemplate} {...props} />;
}

function TemplatesSheet({
  vertical,
  indicatorDataSource,
  financialsTemplate,
  systemTemplate,
  overridingTemplate,
  renderingTemplate,
  onChange,
}: TemplatesProps) {
  const intl = useIntl();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<IFinancialsTemplateFieldsFragment | null>(null);
  const [selectedTab, setSelectedTab] = useState<TemplatesEnum>(TemplatesEnum.MyTemplates);
  const {
    templates,
    breakdown,
    loading,
    refetch: refetchOwnTemplates,
  } = useOwnTemplates({
    filters: {
      filterByPlatformVertical: vertical,
      filterByTemplateCodes: systemTemplate?.templateCodes || null,
    },
  });
  const {
    templates: starredTemplates,
    loading: starredTemplatesLoading,
    refetch: refetchStarredTemplates,
    totalCount: starredTemplatesTotalCount,
  } = useStarredTemplates({
    filters: {
      filterByPlatformVertical: vertical,
      filterByTemplateCodes: systemTemplate?.templateCodes || null,
      filterByCountry: null,
      filterByNamespace: null,
      filterByViewerArchivedTemplates: null,
      filterByViewerId: null,
    },
    orderBy: null,
    limit: 30,
    cursor: null,
  });

  const initialVariables = useMemo(
    () => ({
      filters: {
        filterByCountry: null,
        filterByTemplateCodes: systemTemplate?.templateCodes || null,
        filterByPlatformVertical: vertical,
        filterByViewerArchivedTemplates: null,
        filterByViewerId: null,
        // Keep system templates hidden
        filterByNamespace: [IFinancialsTemplateNamespace.Fundamentei, IFinancialsTemplateNamespace.User],
      },
      orderBy: {
        field: IFinancialsTemplatesQueryOrderField.UseCount,
        direction: IOrderDirection.Descending,
      },
      limit: 20,
      cursor: null,
    }),
    [vertical, indicatorDataSource, systemTemplate?.templateCodes],
  );

  const {
    templates: publicTemplates,
    loading: publicTemplatesLoading,
    refetch: refetchPublicTemplates,
    fetchMore: fetchMorePublicTemplates,
    variables: publicTemplatesVariables,
    pageInfo: publicTemplatesPageInfo,
  } = usePublicTemplates(initialVariables);

  const { templates: ourTemplates } = usePublicTemplates({
    filters: {
      filterByCountry: null,
      filterByTemplateCodes: systemTemplate?.templateCodes || null,
      filterByPlatformVertical: vertical,
      filterByViewerArchivedTemplates: null,
      filterByViewerId: null,
      filterByNamespace: [IFinancialsTemplateNamespace.Fundamentei],
    },
    orderBy: {
      field: IFinancialsTemplatesQueryOrderField.UseCount,
      direction: IOrderDirection.Descending,
    },
    limit: 5,
    cursor: null,
  });

  const { setAsDefaultFinancialsTemplate } = useTemplateActions();

  const [addFinancialsTemplate] = useMutation<IAddFinancialsTemplateMutation, IAddFinancialsTemplateMutationVariables>(
    AddFinancialsTemplateMutation,
  );
  const [editFinancialsTemplate] = useMutation<
    IEditFinancialsTemplateMutation,
    IEditFinancialsTemplateMutationVariables
  >(EditFinancialsTemplateMutation);

  const isDefiningTemplate = isCreatingTemplate || editingTemplate !== null;

  const viewerCanResetToOriginal =
    (systemTemplate &&
      overridingTemplate &&
      renderingTemplate &&
      overridingTemplate.objectId !== systemTemplate.objectId &&
      renderingTemplate.objectId === overridingTemplate.objectId) ||
    (systemTemplate &&
      renderingTemplate &&
      renderingTemplate.overrideCriteria &&
      renderingTemplate.objectId !== systemTemplate.objectId);
  const viewerCanPause = systemTemplate && renderingTemplate && systemTemplate.objectId !== renderingTemplate.objectId;
  const isSystemTemplateActive = !!renderingTemplate && renderingTemplate.objectId !== systemTemplate?.objectId;

  return (
    <Sheet
      modal={true}
      open={isSheetOpen}
      onOpenChange={() => {
        setIsSheetOpen(false);
        setIsCreatingTemplate(false);
        setEditingTemplate(null);
      }}
    >
      <div className="flex h-10 w-full flex-nowrap items-center rounded-xl sm:w-auto">
        <button
          type="button"
          disabled={loading}
          onClick={() => setIsSheetOpen(!isSheetOpen)}
          className={classNames(
            "relative flex h-12 w-full flex-col items-start justify-center px-2 py-1 text-slate-950 md:h-10 lg:w-40",
            "min-w-40",
            "rounded-l-xl border border-slate-200 bg-slate-50/60",
            "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
            "focus-visible:outline-none",
            "focus-visible:ring-1 focus-visible:ring-offset-1",
            "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
            "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
            "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
            "disabled:cursor-not-allowed disabled:opacity-50",
            { "dark:bg-sky-600/20": isSystemTemplateActive },
          )}
        >
          {renderingTemplate && renderingTemplate.name && (
            <h1
              className={classNames(
                "line-clamp-1 w-full whitespace-nowrap text-left text-xs font-bold tracking-wider dark:text-slate-200",
                { "dark:text-sky-300": isSystemTemplateActive },
              )}
            >
              {renderingTemplate.name}
            </h1>
          )}
          <p className="whitespace-nowrap text-xs font-medium tracking-wider text-gray-600 dark:text-slate-400">
            <FormattedMessage
              id="ZIsIUZ"
              defaultMessage="Template"
              description="Button subtitle higlighting the selected template"
            />
          </p>
        </button>

        <QuickTemplateSwitcher
          templates={starredTemplates.filter(({ archivedAt }) => archivedAt === null)}
          offerableTemplates={ourTemplates}
          setIsSheetOpen={setIsSheetOpen}
          setSelectedTab={setSelectedTab}
          onChange={onChange}
        />

        {viewerCanResetToOriginal ? (
          <SimpleTextTooltip
            delayDuration={600}
            content={intl.formatMessage({
              defaultMessage: "Voltar para o Padrão do Sistema",
              id: "iXtuxN",
            })}
          >
            <button
              type="button"
              onClick={() => {
                setAsDefaultFinancialsTemplate({
                  variables: {
                    objectId: systemTemplate.objectId,
                    input: {
                      platformVertical: systemTemplate.vertical,
                    },
                  },
                });

                onChange?.(systemTemplate);
              }}
              className={classNames(
                "group inline-flex",
                "relative -ml-px h-12 items-center bg-slate-50/60 p-2 px-4 text-slate-950 md:h-10 md:px-2",
                "rounded-r-xl border  border-slate-200",
                "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                "focus-visible:outline-none",
                "focus-visible:ring-1 focus-visible:ring-offset-1",
                "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
                "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                "disabled:cursor-not-allowed",
              )}
            >
              <Undo2Icon
                strokeWidth={2}
                absoluteStrokeWidth={true}
                className={classNames(
                  "h-5",
                  "w-5",

                  "stroke-violet-500 dark:stroke-violet-300",
                  "group-hover:opacity-80 disabled:group-hover:opacity-100",
                  "dark:group-hover:stroke-white",
                  "group-disabled:stroke-slate-300 dark:group-disabled:stroke-slate-400",
                )}
              />
            </button>
          </SimpleTextTooltip>
        ) : (
          <button
            type="button"
            disabled={!renderingTemplate || !viewerCanPause}
            onClick={() => {
              onChange?.(null);
            }}
            className={classNames(
              "group inline-flex",
              "relative -ml-px h-12 items-center bg-slate-50/60 p-2 px-4 text-slate-950 md:h-10 md:px-2 lg:px-2",
              "rounded-r-xl border  border-slate-200",
              "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
              "focus-visible:outline-none",
              "focus-visible:ring-1 focus-visible:ring-offset-1",
              "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
              "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
              "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
              "disabled:cursor-not-allowed",
            )}
          >
            <PauseIcon
              strokeWidth={2}
              absoluteStrokeWidth={true}
              className={classNames(
                "h-5 w-5",
                "fill-sky-500 stroke-sky-500 dark:fill-sky-300 dark:stroke-sky-300",
                "group-hover:opacity-80 disabled:group-hover:opacity-100",
                "dark:group-hover:fill-white dark:group-hover:stroke-white",
                "group-disabled:fill-slate-50 group-disabled:stroke-slate-300 dark:group-disabled:fill-slate-700 dark:group-disabled:stroke-slate-400",
              )}
            />
          </button>
        )}
      </div>

      <SheetContent
        position="right"
        className={classNames(
          "h-dvh w-screen space-y-6 overflow-y-auto border-l border-slate-300 bg-white p-4 pt-8 outline-none",
          "sm:p-10",
          "xl:w-3/6",
          "dark:border-slate-800 dark:bg-slate-950",
        )}
        onClick={(event) => event.stopPropagation()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        {isDefiningTemplate ? (
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="text-3xl font-black text-slate-800 dark:text-white">
              {editingTemplate && <FormattedMessage defaultMessage="Editar Template" id="IK2tas" />}
              {isCreatingTemplate && <FormattedMessage defaultMessage="Novo Template" id="BqxNlO" />}
            </SheetTitle>
          </SheetHeader>
        ) : (
          <SheetHeader className="space-y-1 text-left">
            <div className="flex items-center gap-x-4">
              <SheetTitle className="pr-12 text-2xl font-black text-slate-800 dark:text-white sm:pr-0 sm:text-3xl">
                <FormattedMessage
                  defaultMessage="Templates para {vertical}"
                  id="c9mgeD"
                  values={{
                    vertical: getPlatformVerticalTitle(vertical),
                  }}
                />
              </SheetTitle>

              <RefreshButton
                loading={loading}
                className="hidden sm:flex"
                onClick={() => {
                  refetchOwnTemplates({});
                  refetchPublicTemplates({});
                  refetchStarredTemplates();
                }}
              />
            </div>

            <SheetDescription>
              <FormattedMessage
                defaultMessage="Clique no botão abaixo para criar um template do zero com os indicadores que quiser. Ou favorite os templates do Fundamentei e dos usuários do site. Você também pode definir como padrão ou copiar para os seus templates para editar os indicadores."
                id="udIpeB"
              />
            </SheetDescription>
          </SheetHeader>
        )}

        {!isDefiningTemplate && (
          <div className="flex flex-col gap-y-2">
            <Button
              size="full"
              variant="ghost"
              className="bg-indigo-50 text-indigo-600 hover:opacity-80 dark:bg-indigo-800/20 dark:text-indigo-200"
              onClick={() => setIsCreatingTemplate(true)}
            >
              <FormattedMessage defaultMessage="Criar novo template" id="Jq5BJN" />
            </Button>
          </div>
        )}

        {!isDefiningTemplate && (
          <div>
            <Tabs
              className="w-full space-y-4 rounded-xl font-normal"
              defaultValue="ACTIVE"
              value={selectedTab}
              onValueChange={(tab) => {
                if (tab === "ACTIVE" || tab === "ARCHIVED") {
                  refetchOwnTemplates();
                }
                if (tab === "STARRED") {
                  refetchStarredTemplates();
                }

                setSelectedTab(tab as TemplatesEnum);
              }}
            >
              <>
                <TabsList className="hidden w-full flex-1 lg:flex">
                  <TabsTrigger className="w-full" value={TemplatesEnum.Public}>
                    <FormattedMessage
                      defaultMessage="Públicos ({count})"
                      id="BG/Hb6"
                      values={{
                        count: breakdown?.publicCount || 0,
                      }}
                    />
                  </TabsTrigger>
                  <TabsTrigger className="w-full" value={TemplatesEnum.StarredTemplates}>
                    <FormattedMessage
                      defaultMessage="Favoritados ({count})"
                      id="+CcnmB"
                      values={{
                        count: starredTemplatesTotalCount || 0,
                      }}
                    />
                  </TabsTrigger>
                  <TabsTrigger className="w-full" value={TemplatesEnum.MyTemplates}>
                    <FormattedMessage
                      defaultMessage="Meus Templates ({count})"
                      id="kwc8o0"
                      values={{
                        count: breakdown?.myTemplatesCount || 0,
                      }}
                    />
                  </TabsTrigger>
                  <TabsTrigger className="w-full" value={TemplatesEnum.ArchivedTemplates}>
                    <FormattedMessage
                      defaultMessage="Arquivados ({count})"
                      id="PtomVm"
                      values={{
                        count: breakdown?.archivedCount || 0,
                      }}
                    />
                  </TabsTrigger>
                </TabsList>

                <NativeSelect
                  value={selectedTab}
                  className="w-full sm:hidden"
                  onChange={(event) => setSelectedTab(event.target.value as TemplatesEnum)}
                >
                  <option value={TemplatesEnum.Public}>
                    <FormattedMessage
                      defaultMessage="Públicos ({count})"
                      id="BG/Hb6"
                      values={{
                        count: breakdown?.publicCount || 0,
                      }}
                    />
                  </option>
                  <option value={TemplatesEnum.StarredTemplates}>
                    <FormattedMessage
                      defaultMessage="Favoritados ({count})"
                      id="+CcnmB"
                      values={{
                        count: starredTemplatesTotalCount || 0,
                      }}
                    />
                  </option>
                  <option value={TemplatesEnum.MyTemplates}>
                    <FormattedMessage
                      defaultMessage="Meus Templates ({count})"
                      id="kwc8o0"
                      values={{
                        count: breakdown?.myTemplatesCount || 0,
                      }}
                    />
                  </option>
                  <option value={TemplatesEnum.ArchivedTemplates}>
                    <FormattedMessage
                      defaultMessage="Arquivados ({count})"
                      id="PtomVm"
                      values={{
                        count: breakdown?.archivedCount || 0,
                      }}
                    />
                  </option>
                </NativeSelect>
              </>

              <TabsContent className="border-none p-0" value={TemplatesEnum.Public}>
                <TemplatesList
                  systemTemplate={systemTemplate}
                  overridingTemplate={overridingTemplate}
                  renderingTemplate={renderingTemplate}
                  inheritedTemplate={financialsTemplate}
                  templates={publicTemplates}
                  templateTab={TemplatesEnum.Public}
                  isLoading={publicTemplatesLoading}
                  onEditClick={setEditingTemplate}
                  onToggleSheetOpen={() => setIsSheetOpen(!isSheetOpen)}
                  onRefetch={() => {
                    refetchOwnTemplates();
                    setSelectedTab(TemplatesEnum.MyTemplates);
                  }}
                  onRefetchStarredTemplates={() => refetchStarredTemplates()}
                  hasNextPage={publicTemplatesPageInfo?.hasNextPage}
                  onFetchMore={() => {
                    fetchMorePublicTemplates({
                      variables: {
                        cursor: publicTemplatesPageInfo?.cursor,
                      },
                    });
                  }}
                  setSelectedTab={setSelectedTab}
                  onApplyTemplate={onChange}
                >
                  {publicTemplatesVariables && (
                    <QueryVariablesContext.Provider
                      value={[refetchPublicTemplates, initialVariables, publicTemplatesVariables]}
                    >
                      <Filters />
                    </QueryVariablesContext.Provider>
                  )}
                </TemplatesList>
              </TabsContent>

              <TabsContent className="border-none p-0" value={TemplatesEnum.StarredTemplates}>
                <TemplatesList
                  systemTemplate={systemTemplate}
                  overridingTemplate={overridingTemplate}
                  renderingTemplate={renderingTemplate}
                  inheritedTemplate={financialsTemplate}
                  templates={starredTemplates.filter(({ archivedAt }) => archivedAt === null)}
                  templateTab={TemplatesEnum.StarredTemplates}
                  isLoading={starredTemplatesLoading}
                  onEditClick={setEditingTemplate}
                  onToggleSheetOpen={() => setIsSheetOpen(!isSheetOpen)}
                  onRefetch={() => refetchOwnTemplates()}
                  onRefetchStarredTemplates={() => refetchStarredTemplates()}
                  setSelectedTab={setSelectedTab}
                  onApplyTemplate={onChange}
                />
              </TabsContent>

              <TabsContent className="border-none p-0" value={TemplatesEnum.MyTemplates}>
                <TemplatesList
                  systemTemplate={systemTemplate}
                  overridingTemplate={overridingTemplate}
                  renderingTemplate={renderingTemplate}
                  inheritedTemplate={financialsTemplate}
                  templates={templates.filter(({ archivedAt }) => archivedAt === null)}
                  templateTab={TemplatesEnum.MyTemplates}
                  isLoading={loading}
                  onEditClick={setEditingTemplate}
                  onToggleSheetOpen={() => setIsSheetOpen(!isSheetOpen)}
                  onRefetch={() => refetchOwnTemplates()}
                  onRefetchStarredTemplates={() => refetchStarredTemplates()}
                  setSelectedTab={setSelectedTab}
                  onApplyTemplate={onChange}
                />
              </TabsContent>

              <TabsContent className="border-none p-0" value={TemplatesEnum.ArchivedTemplates}>
                <TemplatesList
                  systemTemplate={systemTemplate}
                  overridingTemplate={overridingTemplate}
                  renderingTemplate={renderingTemplate}
                  inheritedTemplate={financialsTemplate}
                  templates={templates.filter(({ archivedAt }) => archivedAt !== null)}
                  templateTab={TemplatesEnum.ArchivedTemplates}
                  isLoading={loading}
                  onEditClick={setEditingTemplate}
                  onToggleSheetOpen={() => setIsSheetOpen(!isSheetOpen)}
                  onRefetch={() => refetchOwnTemplates()}
                  setSelectedTab={setSelectedTab}
                  onApplyTemplate={onChange}
                />
              </TabsContent>
            </Tabs>
          </div>
        )}

        {editingTemplate && (
          <Provider>
            <DefineTemplateForm
              defaultValues={{
                name: editingTemplate.name,
                description: editingTemplate.description,
                indicators: editingTemplate.indicators,
              }}
              onCancel={() => setEditingTemplate(null)}
              onSubmit={({ name, description, indicators }) => {
                if (!financialsTemplate) {
                  return;
                }

                setEditingTemplate(null);
                editFinancialsTemplate({
                  variables: {
                    objectId: editingTemplate.objectId,
                    input: {
                      templateCodes: financialsTemplate.templateCodes,
                      name,
                      description,
                      indicators: indicators.map(({ objectId }) => objectId),
                      vertical: financialsTemplate.vertical,
                      visibility: IFinancialsTemplateVisibility.Private,
                      countryCode: ISecurityCountry.Us,
                      betterSuitedFor: [],
                      overrideCriteria: null,
                    },
                  },
                  onCompleted: () => refetchOwnTemplates(),
                });
              }}
            />
          </Provider>
        )}

        {isCreatingTemplate && (
          <Provider>
            <DefineTemplateForm
              filters={{
                filterByIndicatorVertical: null,
                filterByDataSource: [
                  indicatorDataSource,
                  IIndicatorDataSource.Global,
                  IIndicatorDataSource.Manual,
                  IIndicatorDataSource.Other,
                  IIndicatorDataSource.Unspecified,
                ],
              }}
              defaultValues={{
                name: "",
                description: "",
                indicators: [],
              }}
              onCancel={() => setIsCreatingTemplate(false)}
              onSubmit={({ name, description, indicators }) => {
                if (!financialsTemplate) {
                  return;
                }

                setIsCreatingTemplate(false);
                addFinancialsTemplate({
                  variables: {
                    input: {
                      templateCodes: financialsTemplate.templateCodes,
                      name,
                      description,
                      indicators: indicators.map(({ objectId }) => objectId),
                      vertical: financialsTemplate.vertical,
                      visibility: IFinancialsTemplateVisibility.Private,
                      countryCode: ISecurityCountry.Us,
                      betterSuitedFor: [],
                      overrideCriteria: null,
                    },
                  },
                  onCompleted: () => refetchOwnTemplates(),
                });
              }}
            />
          </Provider>
        )}
      </SheetContent>
    </Sheet>
  );
}

function useStarredTemplates(initialVariables?: IFinancialsTemplatesQueryVariables) {
  const { data, loading, refetch } = useQuery<IStarredTemplatesQuery, IStarredTemplatesQueryVariables>(
    StarredTemplatesQuery,
    {
      variables: initialVariables,
    },
  );
  return {
    templates: data?.selfie?.starredTemplates.edges.map((edge) => edge.node) || [],
    loading,
    refetch,
    totalCount: data?.selfie?.starredTemplates.totalCount,
  };
}

function useOwnTemplates(initialVariables?: DeepPartial<IFinancialsTemplatesQueryVariables>) {
  const viewer = useSelfie();
  const { data, loading, refetch } = useQuery<IUserFinancialsTemplatesQuery, IUserFinancialsTemplatesQueryVariables>(
    UserFinancialsTemplatesQuery,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        ...initialVariables,
        filters: merge(
          {
            filterByNamespace: [IFinancialsTemplateNamespace.User],
            filterByViewerId: viewer?._id,
            filterByViewerArchivedTemplates: true,
            filterByPlatformVertical: null,
            filterByTemplateCodes: null,
            filterByCountry: null,
          },
          initialVariables?.filters,
        ),
        orderBy: {
          direction: IOrderDirection.Descending,
          field: IFinancialsTemplatesQueryOrderField.CreatedAt,
          ...initialVariables?.orderBy,
        },
        limit: 30,
        cursor: null,
      },
    },
  );

  return {
    templates: data?.financialsTemplates?.nodes || [],
    breakdown: data?.financialsTemplates?.breakdown,
    loading,
    refetch,
  };
}

function usePublicTemplates(variables: IFinancialsTemplatesQueryVariables) {
  const {
    data,
    fetchMore,
    loading,
    refetch,
    variables: queryVariables,
    networkStatus,
  } = useQuery<IFinancialsTemplatesQuery, IFinancialsTemplatesQueryVariables>(FinancialsTemplatesQuery, {
    notifyOnNetworkStatusChange: true,
    variables,
  });

  return {
    templates: data?.financialsTemplates?.nodes || [],
    loading: loading && networkStatus !== NetworkStatus.fetchMore,
    refetch,
    fetchMore,
    variables: queryVariables,
    networkStatus,
    pageInfo: data?.financialsTemplates?.pageInfo,
    totalCount: data?.financialsTemplates.totalCount,
  };
}
